/**
 * 文件操作相关工具函数
 */

// 用于存储创建的Object URL，以便后续清理
const objectUrls = new Set<string>();

// 清理所有存储的Object URL
export const cleanupObjectUrls = () : void => {
  objectUrls.forEach(url => {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      console.warn('清理Object URL失败:', error);
    }
  });
  objectUrls.clear();
};

/**
 * 获取图片预览URL
 * @param value 文件值（可以是字符串URL、File对象或Blob对象）
 * @returns 图片预览URL
 */
export const getImagePreviewUrl = (value : any) : string => {
  if (!value) return '';

  // 如果已经是字符串URL，直接返回
  if (typeof value === 'string') {
    return value;
  }

  // 如果是File对象或Blob对象，创建Object URL
  if (value instanceof File || value instanceof Blob) {
    const url = URL.createObjectURL(value);
    objectUrls.add(url);
    return url;
  }

  return '';
};

// 清理单个Object URL
export const cleanupObjectUrl = (url : string) : void => {
  if (objectUrls.has(url)) {
    try {
      URL.revokeObjectURL(url);
      objectUrls.delete(url);
    } catch (error) {
      console.warn('清理Object URL失败:', error);
    }
  }
};

/**
 * 触发链接下载的辅助函数
 * @param url 下载地址
 * @param fileName 文件名
 * @param cleanupFunction 清理函数（可选），用于清理临时资源
 * @param revokeBlobUrl 是否需要撤销Blob URL，默认为false
 */
const triggerLinkDownload = (url: string, fileName: string, cleanupFunction?: () => void, revokeBlobUrl: boolean = false): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  
  document.body.appendChild(link);
  
  // 使用更可靠的点击触发方式
  if (link.click) {
    link.click();
  } else {
    // 降级方案：创建MouseEvent
    const event = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    link.dispatchEvent(event);
  }
  
  // 延迟清理资源，确保下载完成
  setTimeout(() => {
    if (document.body.contains(link)) {
      document.body.removeChild(link);
    }
    // 如果需要撤销Blob URL
    if (revokeBlobUrl) {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        console.warn('清理Blob URL失败:', e);
      }
    }
    // 执行清理函数
    if (cleanupFunction) {
      try {
        cleanupFunction();
      } catch (e) {
        console.warn('执行清理函数失败:', e);
      }
    }
  }, 1000);
};

/**
 * 创建下载链接并触发下载
 * @param url 下载地址
 * @param fileName 文件名
 * @param cleanupFunction 清理函数（可选），用于清理临时资源
 * @returns boolean 是否下载成功
 */
const createDownloadLink = async (url : string, fileName : string, cleanupFunction ?: () => void) : Promise<boolean> => {
  try {
    // 对于PDF等浏览器可能直接预览的文件，使用fetch API获取并转换为Blob
    const fileExt = fileName.split('.').pop()?.toLowerCase();
    const forceDownloadExtensions = ['doc', 'docx', 'pdf']; // 'doc', 'docx'
    
    if (typeof url === 'string' && forceDownloadExtensions.includes(fileExt || '')) {
      try {
        // 使用fetch API获取文件内容，添加更多头部信息确保强制下载
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/octet-stream',
            'Content-Disposition': `attachment; filename="${encodeURIComponent(fileName)}"`
          },
        });
        
        if (!response.ok) {
          throw new Error(`下载失败: ${response.statusText}`);
        }
        
        // 转换为Blob对象，确保设置正确的MIME类型
        const blob = await response.blob();
        // 根据文件扩展名设置正确的MIME类型
        let fileType = blob.type || 'application/octet-stream';
        if (fileExt === 'doc') {
          fileType = 'application/msword';
        } else if (fileExt === 'docx') {
          fileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        }
        const typedBlob = new Blob([blob], { type: fileType });
        
        // 优先尝试使用navigator.msSaveBlob（IE兼容）或navigator.saveBlob
        if (typeof navigator !== 'undefined' && 'msSaveBlob' in navigator) {
          // IE浏览器
          (navigator as any).msSaveBlob(typedBlob, fileName);
          console.log(`IE浏览器下载文件: ${fileName}`);
          if (cleanupFunction) {
            cleanupFunction();
          }
          return true;
        } else if (typeof navigator !== 'undefined' && 'saveBlob' in navigator) {
          // 其他支持saveBlob的浏览器
          (navigator as any).saveBlob(typedBlob, fileName);
          console.log(`使用navigator.saveBlob下载文件: ${fileName}`);
          if (cleanupFunction) {
            cleanupFunction();
          }
          return true;
        } else {
          // 标准浏览器，使用Blob URL和a标签
          const blobUrl = URL.createObjectURL(typedBlob);
          triggerLinkDownload(blobUrl, fileName, cleanupFunction, true);
        }
      } catch (fetchError) {
        // 捕获可能的跨域错误或其他fetch相关错误
        console.warn(`fetch请求失败(可能是跨域问题)，使用备选方案: ${fetchError}`);
        // 使用triggerLinkDownload作为备选方案
        triggerLinkDownload(url, fileName, cleanupFunction);
      }
    } else {
      // 对于其他文件类型，使用更可靠的下载方式
      triggerLinkDownload(url, fileName, cleanupFunction);
    }
    
    console.log(`下载文件: ${fileName}`);
    return true;
  } catch (error) {
    console.error('创建下载链接失败:', error);
    return false;
  }
};

/**
 * 下载文件 - 支持File对象或URL字符串
 * @param params 下载参数
 * @param params.file 文件（File对象或URL字符串）
 * @param params.fileName 可选的文件名
 * @param params.isLargeFile 是否为大文件（启用分块下载），仅对URL下载有效
 * @param params.chunkSize 分块大小（字节），默认为5MB，仅对URL下载有效
 * @param params.onProgress 下载进度回调函数，仅对URL下载有效
 * @param params.onChunkProgress 分块下载进度回调函数，仅对URL下载有效
 * @returns Promise<boolean> 下载是否成功
 */
export const downloadFile = async (params : {
  file : File | Blob | string;
  fileName ?: string;
}) : Promise<boolean> => {
  const {
    file,
    fileName: customFileName,
  } = params;

  try {
    // 判断是File对象/Blob对象还是URL字符串
    if (file instanceof File || file instanceof Blob) {
      // 如果是File对象，可以直接使用其name属性
      const fileName = customFileName || (file instanceof File ? file.name : 'download_' + new Date().getTime());

      const _file = new File([file], fileName, {
        type: file.type
      })
      const url = URL.createObjectURL(_file);
      return await createDownloadLink(url, _file.name, () => cleanupObjectUrl(url));
    } else if (typeof file === 'string') {
      // URL字符串下载
      const fileUrl = file;
      const fileName = customFileName || fileUrl.split('/').pop() || 'download_' + new Date().getTime();
      return await createDownloadLink(fileUrl, fileName);
    } else {
      console.error('下载文件失败: 文件类型不支持');
      return false;
    }
  } catch (error) {
    console.error('下载文件失败:', error);
    return false;
  }
};

/**
 * 判断是否为图片文件
 * @param file 文件（File对象或URL字符串）
 * @returns 是否为图片文件
 */
export const isImageFile = (file : File | string) : boolean => {
  const imageTypes = ['image/jpeg', 'image/png', 'image/jpg'];
  const imageExtensions = ['.jpg', '.jpeg', '.png'];
  
  if (typeof file === 'string') {
    // 如果是URL字符串，根据扩展名判断
    const lowerCaseFile = file.toLowerCase();
    return imageExtensions.some(ext => lowerCaseFile.endsWith(ext));
  }
  // 如果是File对象，根据MIME类型判断
  return imageTypes.includes(file.type);
};

/**
 * 获取文件名
 * @param file 文件（File对象或URL字符串）
 * @returns 文件名
 */
export const getFileName = (file : File | string | null | undefined) : string => {
  if (!file) {
    return '';
  }
  if (typeof file === 'string') {
    // 如果是URL字符串，提取文件名
    return file.split('/').pop() || file;
  }
  // 如果是File对象，返回文件名
  return file.name;
};